/*
      ___           ___
     /\  \         /\  \
    /::\  \       /::\  \
   /:/\:\  \     /:/\ \  \
  /::\ \:\  \   _\:\ \ \  \
 /:/\:\ \:\__\ /\ \:\ \ \__\
 \/_|::\/:/  / \:\ \:\ \/__/
    |:|::/  /   \:\ \:\__\
    |:|\/__/     \:\/:/  /
    |:|  |        \::/  /
     \|__|         \/__/

    Developed by Ralph Shippam - 2019.
*/

import {
    Injectable,
    ComponentFactoryResolver,
    ApplicationRef,
    Injector,
} from '@angular/core';
import {MatDialog, MatDialogRef, MatDialogConfig} from '@angular/material/dialog';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {RSDialogComponent} from './rsdialog.component';
import {RSBottomSheetComponent} from './rsbottomsheet.component';
import {DomSanitizer} from '@angular/platform-browser';
import {RSDialog, RSBottomSheet} from './rsdialog';
import DialogHandles = RSDialog.DialogHandles;
import {Subject} from 'rxjs';

@Injectable({
   providedIn: 'root',
})
export class RSDialogService {

    constructor(
        private dialog: MatDialog,
        private bottomsheet: MatBottomSheet,
        private sanitizer: DomSanitizer,
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector
    ) {}

    public showDialog(config: RSDialog.Options);
    public showDialog(message: string, title?: string);
    public showDialog(configOrMessage: any, title?: string, additional?: string): Promise<any> {
        if (configOrMessage && typeof configOrMessage === 'string') {
            // Message - send via handler
            return this.showMessageDialog(configOrMessage, additional, title);
        }

        // Promise for result
        return new Promise((resolve, reject) => {
            // Config
            const dialogOptions: RSDialog.Options = configOrMessage;
            let dialogRef: MatDialogRef<any>;

            // Dialog Type
            const dialogType = dialogOptions.type;

            // Build dialog handles
            const dialogHandles = {
                close: (aValue) => {
                    if (typeof aValue === 'undefined') {
                        aValue = RSDialog.defaultValues.CANCEL;
                    }
                    dialogRef.close(aValue);
                },
                buttonTemplateRef: undefined,
                buttonTemplateAttach: () => {
                    // Just mirror the value out to our data object
                    if (dialogHandles.buttonTemplateRef) {
                        config.data.buttonTemplateRef = dialogHandles.buttonTemplateRef;
                    } else {
                        config.data.buttonTemplateRef = undefined;
                    }
                },
                onScroll: new Subject<Event>()
            } as DialogHandles;
            if (dialogOptions.close && typeof dialogOptions.close === 'function') {
                // User defined one
                dialogHandles.close = dialogOptions.close;
            }

            // Create Config
            const config = {
                id: dialogOptions.id || 'rsdialog' + (+new Date()),
                width: dialogOptions.width || (dialogOptions.size ? dialogOptions.size.getWidth() : 'auto'),
                maxWidth: dialogOptions.maxWidth || '90vw',
                height: dialogOptions.height || (dialogOptions.size ? dialogOptions.size.getHeight() : 'auto'),
                maxHeight: dialogOptions.maxHeight || '90vh',
                data: {
                    buttonTemplateRef: undefined,
                    type: dialogType,
                    title: dialogOptions.title,
                    message: dialogOptions.message,
                    additional: dialogOptions.additional || additional,
                    html: !!dialogOptions.html,
                    buttons: dialogOptions.buttons,
                    hasIcons: dialogOptions.hasIcons,
                    style: dialogOptions.style,
                    styles: RSDialog.Style,
                    component: !!dialogOptions.component,
                    values: dialogOptions.values || {},
                    inputType: dialogOptions.inputType || '',
                    inputValue: dialogOptions.inputValue || '',
                    inputRegex: dialogOptions.inputRegex || '',
                    inputPlaceholder: dialogOptions.inputPlaceholder || '',
                    cancelable: dialogOptions.cancelable,
                    dialogHandles,
                    closeButton: !!dialogOptions.closeButton,
                    scrollAlwaysVisible: !!dialogOptions.scrollAlwaysVisible
                },
                disableClose: !dialogOptions.cancelable,
                closeOnNavigation: !!dialogOptions.closeOnNavigation,
                hasBackdrop: !!dialogOptions.hasBackdrop,
                autoFocus: !!dialogOptions.autoFocus,
                backdropClass: dialogOptions.backdropClass,
                role: ( dialogOptions.type !== RSDialog.Type.PROMPT  ) ? 'dialog' : 'alertdialog',
                panelClass: [dialogOptions.panelClass, (!dialogOptions.hasBackground ? 'no-background' : ''), (!dialogOptions.hasShadow ? 'no-shadow' : '')].filter(c => !!c),

                // scrollStrategy: dialogOptions.scrollStrategy || NoopScrollStrategy
                // position: DialogPosition
                // id: string
                // direction: Direction
            } as MatDialogConfig;

            // Do we need to inject our component element
            let componentRef;
            if (dialogOptions.component) {
                componentRef = this.componentFactoryResolver.resolveComponentFactory(dialogOptions.component).create(this.injector);

                // Attach component to the appRef so that it's inside the ng component tree
                this.appRef.attachView(componentRef.hostView);

                // Pass the data down the line
                componentRef.instance.dialogValues = config.data.values;
                componentRef.instance.dialogHandles = dialogHandles;

                // Pass in the reference
                config.data.component = componentRef;
            }

            dialogRef = this.dialog.open(RSDialogComponent, config);
            const afterClosed = dialogRef.afterClosed().subscribe(result => {
                console.log('RSDialog::: Event:: Afterclose: ' + result);
                if (typeof result === 'undefined') { result = -1; }

                // Tidy up our component if applicable
                if (typeof componentRef !== 'undefined') {
                    this.appRef.detachView(componentRef.hostView);
                    componentRef.destroy();
                }
                if (afterClosed) {
                    afterClosed.unsubscribe();
                }
                if (afterOpened) {
                    afterOpened.unsubscribe();
                }
                if (backdropClick) {
                    backdropClick.unsubscribe();
                }

                // Send the result;
                if (result === -1) {
                    reject(result); // Cancel = rejections (i.e. no progress)
                } else {
                    if (dialogType === RSDialog.Type.INPUT) {
                        resolve(config.data.inputValue);
                    } else {
                        resolve(result);
                    }
                }
            });
            const afterOpened = dialogRef.afterOpened().subscribe(result => {
                // Set the focus - See comments in setButtonFocus
                /*setTimeout(() => {
                    const sButtonToggle = 'setButtonFocus';
                    if (dialogRef.componentInstance && typeof dialogRef.componentInstance[sButtonToggle] === 'function') {
                        dialogRef.componentInstance[sButtonToggle]();
                    }
                });*/
            });
            const backdropClick = dialogRef.backdropClick().subscribe(result => {
                console.log('RSDialog::: Event:: Backdropclick: ' + result);
            });
        });
    }

    // TODO: BUILD
    public showInput(message: string, value: string | number, type: RSDialog.InputType, placeholder?: string, regex?: string, title?: string, style?: RSDialog.Style): Promise<any> {
        const config = new RSDialog.Options(RSDialog.Type.INPUT);
        config.inputType = type || RSDialog.InputType.TEXT;
        config.inputPlaceholder = placeholder || 'Input';
        config.inputValue = value || '';
        config.inputRegex = regex || null;

        config.message = message;
        config.title = title || 'Input';
        config.additional = '';
        config.style = style || RSDialog.Style.NORMAL;

        return new Promise<any>(async (resolve, reject) => {
            try {
                const result = await this.showDialog(config);
                resolve(result);
            } catch (e) {
                reject(e.message);
            }
        });
    }

    public showConfirmDialog(message: string, additional?: string | false, title?: string, style?: RSDialog.Style): Promise<boolean> {
        const config = new RSDialog.Options(RSDialog.Type.CONFIRM);
        config.message = message;
        config.title = title || 'Confirm';
        config.additional = additional || '';
        config.style = style || RSDialog.Style.NORMAL;

        return new Promise<boolean>(async (resolve, reject) => {
            try {
                const result = await this.showDialog(config);
                resolve(!!result);
            } catch (e) {
                reject(e.message);
            }
        });
    }

    public showErrorDialog(message: string, additional?: string | false, title?: string): Promise<any> {
        const config = new RSDialog.Options(RSDialog.Type.PROMPT);
        config.size = RSDialog.Size.SMALL;
        config.message = message;
        config.title = title || 'Error';
        config.additional = additional || '';
        config.style = RSDialog.Style.ERROR;
        return this.showDialog(config);
    }

    public showMessageDialog(message: string, additional?: string | false, title?: string, style?: RSDialog.Style): Promise<any> {
        const config = new RSDialog.Options(RSDialog.Type.PROMPT);
        config.message = message;
        config.title = title || 'Message';
        config.additional = additional || '';
        config.style = style || RSDialog.Style.NORMAL;
        return this.showDialog(config);
    }

    public showHTMLDialog(messageHTML: string, title?: string, size?: RSDialog.SizeSpec, style?: RSDialog.Style): Promise<any> {
        const config = new RSDialog.Options(RSDialog.Type.PROMPT);
        config.message = messageHTML;
        config.html = true;
        config.title = title || 'Message';
        config.additional = '';
        config.style = style || RSDialog.Style.NORMAL;
        config.size = size || config.size;
        return this.showDialog(config);
    }

    public topDialogHandle() {
        if (!this.dialog.openDialogs || this.dialog.openDialogs.length === 0) {
            return null;
        }
        return this.dialog.openDialogs[this.dialog.openDialogs.length - 1];
    }

    public getDialogHandle(id: string) {
        if (!this.dialog.openDialogs || this.dialog.openDialogs.length === 0) {
            return null;
        }
        return this.dialog.openDialogs.find(d => d.id === id);
    }

    public isDialogOpen(): boolean {
      return this.dialog.openDialogs.length > 0;
    }

    public showBottomSheet(userConfig: RSBottomSheet.Options): Promise<any> {

        // Promise for result
        return new Promise((resolve, reject) => {
            // Config
            const dialogOptions: RSBottomSheet.Options = userConfig;

            // Create safe or blank links
            dialogOptions.buttons.forEach((button) => {
                if (typeof button.link === 'undefined' || !button.link || !button.link.length) {
                    button.link = 'javascript:;';
                }
                button.link = this.sanitizer.bypassSecurityTrustUrl(button.link).toString();
            });

            // Create Config
            const config = {
                data: {
                    title: dialogOptions.title,
                    message: dialogOptions.message,
                    buttons: dialogOptions.buttons,
                    buttonClick: (value) => {
                        console.log('RSBottomSheet::: buttonClick:: value: ' + value);
                        dialogRef.dismiss(value);
                    }
                },
                disableClose: !dialogOptions.cancelable,
            };

            const dialogRef = this.bottomsheet.open(RSBottomSheetComponent, config);
            dialogRef.backdropClick().subscribe(event => {
                console.log('RSBottomSheet::: Event:: Backdropclick: ');
                if (dialogOptions.cancelable) {
                    dialogRef.dismiss(dialogOptions.cancelValue);
                }
            });
            dialogRef.afterDismissed().subscribe((result) => {
                console.log('RSBottomSheet::: Event:: afterDismissed: ' + result);
                if (typeof result === 'undefined') { result = dialogOptions.cancelValue; }
                resolve(result);
            });
        });
    }

}
